'use client'

import React, { useState, useRef, useEffect } from 'react'
import dynamic from "@/shared/shims/next-dynamic"
import { ChevronLeft, ChevronRight, Maximize2, X, FileText, Download, Upload, Loader2, Info } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/components/ui/tooltip'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover'

const HTMLFlipBook = dynamic(
  (() => import('react-pageflip').then((mod) => (mod && (mod as any).default ? mod : { default: mod }))) as any,
  { ssr: false }
)

interface FlipPageProps {
  children: React.ReactNode
}

const FlipPage = React.forwardRef<HTMLDivElement, FlipPageProps>(({ children }, ref) => (
  <div ref={ref} className="bg-white shadow-2xl">
    {children}
  </div>
))
FlipPage.displayName = 'FlipPage'

export type PreTripFlipbookCardProps = {
  title: string
  pageImageUrls: string[]
  /** When set, Group Leaders can download the PDF from this card */
  pdfDownloadUrl?: string
  /** When set, show Upload PDF and call with selected file (conversion handled by parent) */
  onUpload?: (file: File) => void
  isUploading?: boolean
  /** When set, show an info icon next to the title that shows this tooltip on hover/click */
  infoTooltip?: string
  /** When true and no pages yet, show loading state instead of empty state */
  isLoadingDocuments?: boolean
}

export function PreTripFlipbookCard({
  title,
  pageImageUrls,
  pdfDownloadUrl,
  onUpload,
  isUploading = false,
  infoTooltip,
  isLoadingDocuments = false,
}: PreTripFlipbookCardProps) {
  const [currentPage, setCurrentPage] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [windowWidth, setWindowWidth] = useState(1200)
  const [infoPopoverOpen, setInfoPopoverOpen] = useState(false)
  const flipBookRef = useRef<any>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const totalPages = pageImageUrls.length

  const openFileDialog = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (isUploading || !onUpload) return
    // Defer so we don't blank the page when the native dialog opens (focus/visibility side effects)
    setTimeout(() => fileInputRef.current?.click(), 0)
  }

  // Single-page view for mobile and small desktop (same as Reports Impact Report)
  const SINGLE_PAGE_BREAKPOINT = 1024
  const isSinglePageView = windowWidth < SINGLE_PAGE_BREAKPOINT
  const isMobile = windowWidth < 768

  // When document is replaced, clamp or reset page so flipbook and state stay in sync
  const contentKey = totalPages > 0 ? `${totalPages}-${(pageImageUrls[0] ?? '').slice(-24)}` : 'empty'
  useEffect(() => {
    if (totalPages > 0) {
      setCurrentPage((prev) => (prev >= totalPages ? totalPages - 1 : prev))
    }
  }, [totalPages])
  // Reset to first page when document content changes (e.g. after Replace)
  const prevContentKeyRef = useRef(contentKey)
  useEffect(() => {
    if (contentKey !== prevContentKeyRef.current) {
      prevContentKeyRef.current = contentKey
      setCurrentPage(0)
    }
  }, [contentKey])

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(typeof window !== 'undefined' ? window.innerWidth : 1200)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const goToPrevPage = () => {
    if (flipBookRef.current) flipBookRef.current.pageFlip().flipPrev()
  }

  const goToNextPage = () => {
    if (flipBookRef.current) flipBookRef.current.pageFlip().flipNext()
  }

  const onFlip = (e: { data: number }) => setCurrentPage(e.data)

  const getPageDimensions = () => {
    const padding = 32 // 16px each side for mobile
    const availableWidth = Math.max(280, windowWidth - padding)
    const innerHeight = typeof window !== 'undefined' ? window.innerHeight : 600
    if (isFullscreen) {
      // Single-page fullscreen when below breakpoint
      if (isSinglePageView) {
        return {
          width: Math.min(windowWidth - padding, 500),
          height: Math.min(innerHeight - 120, 660),
        }
      }
      return {
        width: Math.min(600, windowWidth / 2 - 80),
        height: Math.min(800, innerHeight - 180),
      }
    }
    // Single-page view: mobile and small desktop (< 1024px)
    if (isSinglePageView) {
      const width = Math.min(availableWidth, windowWidth < 768 ? 360 : 440)
      const maxHeight = innerHeight - 220
      const height = Math.min(width * 1.35, maxHeight, 600)
      return { width, height }
    }
    // Large desktop: two-page spread
    return { width: 550, height: 730 }
  }

  const { width: pageWidth, height: pageHeight } = getPageDimensions()

  const displayPageInfo = () => {
    if (isSinglePageView) return `Page ${currentPage + 1}`
    if (currentPage === 0) return 'Cover Page'
    const displayStart = currentPage + 1
    const displayEnd = Math.min(currentPage + 2, totalPages)
    return displayStart === displayEnd ? `Page ${displayStart}` : `Pages ${displayStart}-${displayEnd}`
  }

  const wrapperClass = isFullscreen
    ? 'fixed inset-0 z-50 bg-background p-4 overflow-auto'
    : ''

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5 shrink-0" />
            {title}
            {infoTooltip != null && infoTooltip !== '' && (
              <Popover open={infoPopoverOpen} onOpenChange={setInfoPopoverOpen}>
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                          aria-label={`Info about ${title}`}
                        >
                          <Info className="h-3.5 w-3.5" />
                        </button>
                      </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[280px] text-xs">
                      {infoTooltip}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <PopoverContent side="top" className="max-w-[320px] text-sm">
                  {infoTooltip}
                </PopoverContent>
              </Popover>
            )}
          </CardTitle>
          {pdfDownloadUrl ? (
            <Button variant="outline" size="sm" asChild className="shrink-0">
              <a
                href={pdfDownloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="inline-flex items-center gap-1.5"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </a>
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {totalPages === 0 ? (
          isLoadingDocuments ? (
            <div className="rounded-lg border border-dashed bg-muted/50 p-8 text-center text-muted-foreground">
              <Loader2 className="mx-auto h-10 w-10 animate-spin opacity-70" />
              <p className="mt-2 text-sm font-medium">Loading…</p>
            </div>
          ) : (
          <div className="rounded-lg border border-dashed bg-muted/50 p-8 text-center text-muted-foreground space-y-4">
            <FileText className="mx-auto h-10 w-10 opacity-50" />
            <p className="text-sm font-medium">No document yet</p>
            <p className="text-xs">Upload a PDF and it will be turned into a flip book on this page.</p>
            {onUpload ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="sr-only"
                  disabled={isUploading}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      onUpload(file)
                      e.target.value = ''
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={openFileDialog}
                  disabled={isUploading}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 cursor-pointer disabled:opacity-70"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Converting PDF…
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Upload PDF
                    </>
                  )}
                </button>
              </>
            ) : null}
          </div>
          )
        ) : (
          <div className={`flex flex-col gap-4 ${wrapperClass}`}>
            <div className="flex items-center justify-between bg-muted p-2 md:p-4 rounded-lg gap-2">
              <div className="flex items-center gap-1 md:gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPrevPage}
                  disabled={currentPage === 0}
                  showLoader={false}
                  className="h-8 px-2 md:px-3"
                >
                  <ChevronLeft className="w-4 h-4 md:mr-1 shrink-0" />
                  <span className="hidden md:inline">Prev</span>
                </Button>
                <span className="text-xs md:text-sm font-medium px-1 md:px-2 whitespace-nowrap">
                  {isSinglePageView
                    ? `${currentPage + 1}/${totalPages}`
                    : `${displayPageInfo()} of ${totalPages} pages`}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages - 1}
                  showLoader={false}
                  className="h-8 px-2 md:px-3"
                >
                  <span className="hidden md:inline">Next</span>
                  <ChevronRight className="w-4 h-4 md:ml-1" />
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="h-8 px-2 md:px-3"
              >
                {isFullscreen ? <X className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </Button>
            </div>

            <div className="relative flex items-center justify-center bg-gradient-to-b from-gray-100 to-gray-200 p-2 md:p-4 rounded-lg w-full min-h-[300px] sm:min-h-[400px] md:min-h-[560px]">
              {/* Left page-flip arrow */}
              <button
                type="button"
                onClick={goToPrevPage}
                disabled={currentPage === 0}
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-10 md:h-10 rounded-full bg-black/20 hover:bg-black/30 flex items-center justify-center text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-black/20 transition-colors"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
              </button>

              <HTMLFlipBook
                key={`${isSinglePageView ? 's' : 'd'}-${contentKey}`}
                ref={flipBookRef}
                width={pageWidth}
                height={pageHeight}
                size="fixed"
                minWidth={240}
                maxWidth={550}
                minHeight={300}
                maxHeight={730}
                showCover={!isSinglePageView}
                flippingTime={800}
                usePortrait={isSinglePageView}
                startPage={0}
                drawShadow={!isSinglePageView}
                className="shadow-2xl"
                onFlip={onFlip}
                maxShadowOpacity={0.5}
                mobileScrollSupport={true}
                swipeDistance={30}
                clickEventForward={false}
                style={{}}
                startZIndex={0}
                autoSize={false}
                useMouseEvents={true}
                renderOnlyPageLengthChange={false}
                disableFlipByClick={false}
                showPageCorners={true}
              >
                {pageImageUrls.map((imageUrl, index) => (
                  <FlipPage key={`page-${index}`}>
                    <div
                      className="relative bg-white flex items-center justify-center"
                      style={{ width: pageWidth, height: pageHeight }}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element -- plain img avoids Next.js Image 500 on S3 signed URLs */}
                      <img
                        src={imageUrl}
                        alt={`Page ${index + 1}`}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </FlipPage>
                ))}
              </HTMLFlipBook>

              {/* Right page-flip arrow */}
              <button
                type="button"
                onClick={goToNextPage}
                disabled={currentPage >= totalPages - 1}
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-10 md:h-10 rounded-full bg-black/20 hover:bg-black/30 flex items-center justify-center text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-black/20 transition-colors"
                aria-label="Next page"
              >
                <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
              </button>
            </div>

            <div className="text-center text-sm text-muted-foreground space-y-1">
              <p className="font-semibold">📖 {title}</p>
              {isSinglePageView ? (
                <p className="text-xs">Swipe to turn pages • Use arrow buttons to navigate</p>
              ) : (
                <p className="text-xs">
                  Click on page edges or drag corners to turn pages • Use arrow buttons to navigate
                </p>
              )}
              {onUpload ? (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    className="sr-only"
                    disabled={isUploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) {
                        onUpload(file)
                        e.target.value = ''
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={openFileDialog}
                    disabled={isUploading}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs text-primary hover:underline cursor-pointer disabled:opacity-70 bg-transparent border-0 p-0 font-inherit"
                  >
                    {isUploading ? (
                      <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Converting…</>
                    ) : (
                      <><Upload className="h-3.5 w-3.5" /> Replace with another PDF</>
                    )}
                  </button>
                </>
              ) : null}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
